// Lightweight country prefix detection. Uses navigator.language first
// (instant, no network) and falls back to a free public geo-IP endpoint
// only when language doesn't help. Designed to be called once on mount,
// fail-soft, and never block render.

import { PHONE_PREFIXES } from './PhonePrefixSelect';

const ISO_TO_PREFIX = PHONE_PREFIXES.reduce((acc, p) => {
  acc[p.iso] = p.code;
  return acc;
}, {});

const fromNavigatorLanguage = () => {
  try {
    const langs = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language];
    for (const tag of langs) {
      // Examples: "es-ES", "fr-FR", "en-US", "es" (no region)
      const region = (tag || '').split('-')[1];
      if (region && ISO_TO_PREFIX[region.toUpperCase()]) {
        return ISO_TO_PREFIX[region.toUpperCase()];
      }
    }
  } catch { /* ignore */ }
  return null;
};

let cachedPrefix = null;

const fromIpApi = async () => {
  try {
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 2000);
    const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const data = await res.json();
    const iso = (data?.country_code || '').toUpperCase();
    if (iso && ISO_TO_PREFIX[iso]) return ISO_TO_PREFIX[iso];
  } catch { /* ignore network errors */ }
  return null;
};

/**
 * Resolve the user's likely phone prefix.
 * Strategy: cached → navigator.language → IP geo-lookup → null (caller decides default).
 * Never throws; safe to call at mount time.
 */
export async function detectUserPrefix() {
  if (cachedPrefix) return cachedPrefix;
  const fromLang = fromNavigatorLanguage();
  if (fromLang) {
    cachedPrefix = fromLang;
    return fromLang;
  }
  const fromIp = await fromIpApi();
  if (fromIp) {
    cachedPrefix = fromIp;
    return fromIp;
  }
  return null;
}
